import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  RadioGroup,
  Radio,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { ExpandMore, Check, Close, Visibility, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/core/i18n/i18n';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import { useAnyLoading } from '@/core/useAnyLoading';
import {
  Activity,
  Focus,
  FocusRequirementWithDetails,
  Objective,
  CreateFocusDefinitionApprovalInput,
  CreatePlanDefinitionApprovalInput,
  ActivityTypes,
  PlanRequirementDecision,
} from '@shared/schema';
import {
  buildPlanRequirementDecisionLookup,
  filterRequirementsByDecisions,
} from '@/core/helpers/planRequirements';
import CalendarIcon from '@/static/icons/calendar.svg?url';
import Navigation from '../Shared/Navigation.tsx';

interface ApprovalDefinitionsProps {
  objectives: Objective[];
  requirements: FocusRequirementWithDetails[];
  planRequirementDecisions: PlanRequirementDecision[];
  activities: Activity[];
  focuses: Focus[];
  fetchObjectivesByVersionId: (versionId: number) => void;
  getActivitiesByObjectiveId: (objectiveId: number) => void;
  getRequirements: (params?: { planId?: number; year?: number }) => void;
  fetchAllFocuses: (versionId: number) => void;
  fetchPlanRequirementDecisions: (planId: number) => Promise<PlanRequirementDecision[]>;
  addFocusDefinitionApproval: (
    versionId: number,
    data: CreateFocusDefinitionApprovalInput,
  ) => Promise<void>;
  addPlanDefinitionApproval: (
    versionId: number,
    data: CreatePlanDefinitionApprovalInput,
  ) => Promise<void>;
  getFocusDefinitionApprovals: (versionId: number) => Promise<unknown>;
  getPlanDefinitionApproval: (versionId: number) => Promise<unknown>;
  currentVersion: number | null;
  currentPlanId: number | null;
  readOnlyMode: boolean | null;
  currentFulfillmentVersion: number | null;
  selectedYear: string | number;
  currentBusinessUnit?: string | null;
  scopeName: string;
  enterpriseName: string;
}

interface PlanDefinitionApproval {
  Approved: boolean;
  CEOConfirmation?: boolean;
  Comments?: string;
}

interface FocusDefinitionApproval {
  APFocusId: number;
  Approved: boolean;
}

const formatPercentage = (value: number | null | undefined, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Number(num.toFixed(digits));
  const safe = rounded === 0 ? 0 : rounded;
  const str = safe.toFixed(digits);
  return str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

const ApprovalDefinitions: React.FC<ApprovalDefinitionsProps> = ({
  objectives,
  requirements,
  planRequirementDecisions,
  activities,
  focuses,
  fetchObjectivesByVersionId,
  getActivitiesByObjectiveId,
  getRequirements,
  fetchAllFocuses,
  fetchPlanRequirementDecisions,
  addFocusDefinitionApproval,
  addPlanDefinitionApproval,
  getFocusDefinitionApprovals,
  getPlanDefinitionApproval,
  currentVersion,
  currentPlanId,
  readOnlyMode,
  currentFulfillmentVersion,
  selectedYear,
  currentBusinessUnit,
  scopeName,
  enterpriseName,
}) => {
  const isAnyLoading = useAnyLoading();
  const { t, tr } = useTranslation();
  const navigate = useNavigate();
  const [expandedAccordion, setExpandedAccordion] = useState(true);
  const [validationChecked, setValidationChecked] = useState(false);
  const [comments, setComments] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [selectedFocus, setSelectedFocus] = useState<Focus | null>(null);
  const [selectedObjective, setSelectedObjective] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [decisionStatus, setDecisionStatus] = useState<'approve' | 'reject' | null>(null);
  const [desistionByFocus, setDesistionByFocus] = useState<Record<number, 'approve' | 'reject'>>(
    {},
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const [activitiesByObjective, setActivitiesByObjective] = useState<Record<number, Activity[]>>(
    {},
  );
  const fetchedDecisionsForPlan = React.useRef<number | null>(null);
  const decisionsForCurrentPlan = React.useMemo(
    () =>
      (planRequirementDecisions ?? []).filter((decision) =>
        currentPlanId ? decision.APId === currentPlanId : true,
      ),
    [planRequirementDecisions, currentPlanId],
  );
  const decisionLookup = React.useMemo(
    () => buildPlanRequirementDecisionLookup(decisionsForCurrentPlan),
    [decisionsForCurrentPlan],
  );
  const visibleRequirements = React.useMemo(() => {
    if (!requirements || requirements.length === 0) {
      return [] as FocusRequirementWithDetails[];
    }
    if (!currentPlanId || decisionsForCurrentPlan.length === 0) {
      return requirements;
    }
    return filterRequirementsByDecisions(requirements, decisionLookup);
  }, [requirements, decisionsForCurrentPlan, decisionLookup, currentPlanId]);

  const globalAdherence = React.useMemo(() => {
    if (!focuses || focuses.length === 0) return 0;
    let totalWeighted = 0;
    let totalWeight = 0;
    focuses.forEach((f) => {
      const pct = f.AdherencePct ?? 0;
      const weight = f.Weight || 0;
      totalWeighted += pct * weight;
      totalWeight += weight;
    });
    return totalWeight > 0 ? totalWeighted / totalWeight : 0;
  }, [focuses]);

  const allFocusApproved = React.useMemo(
    () => focuses.length > 0 && focuses.every((f) => desistionByFocus[f.APFocusId] === 'approve'),
    [focuses, desistionByFocus],
  );

  useEffect(() => {
    if (!allFocusApproved && decisionStatus !== 'reject') {
      const timeout = setTimeout(() => {
        setDecisionStatus('reject');
      }, 0);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [allFocusApproved, decisionStatus]);

  useEffect(() => {
    if (currentVersion) {
      fetchAllFocuses(currentVersion);
      fetchObjectivesByVersionId(currentVersion);
    }
    const parsedYear = Number.parseInt(String(selectedYear), 10);
    const year = Number.isInteger(parsedYear) ? parsedYear : undefined;
    getRequirements({ planId: currentPlanId ?? undefined, year });
  }, [
    currentVersion,
    fetchAllFocuses,
    fetchObjectivesByVersionId,
    getRequirements,
    currentPlanId,
    selectedYear,
  ]);

  useEffect(() => {
    if (!currentPlanId) return;
    if (decisionsForCurrentPlan.length > 0) {
      fetchedDecisionsForPlan.current = currentPlanId;
      return;
    }
    if (fetchedDecisionsForPlan.current === currentPlanId) return;
    fetchedDecisionsForPlan.current = currentPlanId;
    fetchPlanRequirementDecisions(currentPlanId).catch((error) => {
      console.error('Error fetching plan requirement decisions for approvals:', error);
      fetchedDecisionsForPlan.current = null;
    });
  }, [currentPlanId, decisionsForCurrentPlan, fetchPlanRequirementDecisions]);

  useEffect(() => {
    const loadExistingApprovals = async () => {
      if (!currentVersion) return;

      try {
        const planApprovalRaw = await getPlanDefinitionApproval(currentVersion);
        const planApproval = planApprovalRaw as PlanDefinitionApproval | undefined;
        if (planApproval && typeof planApproval.Approved === 'boolean') {
          setDecisionStatus(planApproval.Approved ? 'approve' : 'reject');
          setValidationChecked(Boolean(planApproval.CEOConfirmation));
          setComments(planApproval.Comments || '');
        }

        const focusApprovalsRaw = (await getFocusDefinitionApprovals(currentVersion)) as
          | FocusDefinitionApproval[]
          | undefined;
        if (Array.isArray(focusApprovalsRaw) && focusApprovalsRaw.length > 0) {
          const approvalsMap: Record<number, 'approve' | 'reject'> = {};
          focusApprovalsRaw.forEach((approval) => {
            approvalsMap[approval.APFocusId] = approval.Approved ? 'approve' : 'reject';
          });
          setDesistionByFocus(approvalsMap);
        }
      } catch (error) {
        console.error('Error loading existing approvals:', error);
      }
    };

    loadExistingApprovals();
  }, [currentVersion, getPlanDefinitionApproval, getFocusDefinitionApprovals]);

  const objectivesByFocus = React.useMemo(() => {
    const grouped: Record<number, Objective[]> = {};
    objectives?.forEach((o) => {
      if (!grouped[o.APFocusId]) grouped[o.APFocusId] = [];
      grouped[o.APFocusId]!.push(o);
    });
    return grouped;
  }, [objectives]);

  useEffect(() => {
    if (activities && activities.length > 0) {
      const first = activities[0];
      if (!first) return;
      const objectiveId: number = first.APObjectiveId;
      const timeout = setTimeout(() => {
        setActivitiesByObjective((prev) => ({ ...prev, [objectiveId]: activities }));
      }, 0);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [activities]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDesistionByFocus((prev) => {
        const initialDecisions: Record<number, 'approve' | 'reject'> = { ...prev };
        focuses.forEach((f) => {
          if (!(f.APFocusId in initialDecisions)) {
            initialDecisions[f.APFocusId] = 'reject';
          }
        });
        return initialDecisions;
      });
    }, 0);
    return () => clearTimeout(timeout);
  }, [focuses]);

  const handleOpenModal = (focus: Focus, index: number) => {
    setSelectedFocus(focus);
    const focusObjectives = objectivesByFocus[focus.APFocusId] || [];
    const firstObjectiveId = focusObjectives[0]?.APObjectiveId || null;
    setSelectedObjective(firstObjectiveId);
    setSelectedIndex(index);
    if (firstObjectiveId) getActivitiesByObjectiveId(firstObjectiveId);
    setAlertMessage(t('ceoValidation.readOnlyMessage'));
    setOpenModal(true);
    setCurrentPage(1);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFocus(null);
    setSelectedObjective(null);
    setSelectedIndex(null);
    setCurrentPage(1);
    setAlertMessage(null);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleExit = () => {
    navigate('/');
  };

  const handleSaveAndFinalize = async () => {
    if (readOnlyMode && currentFulfillmentVersion) {
      navigate('/enter-compliance');

      return;
    }
    if (readOnlyMode && !currentFulfillmentVersion) {
      navigate('/');
      return;
    }

    if (!decisionStatus) {
      setAlertMessage(t('approvalDefinitions.mustDecide'));
      return;
    }

    if (decisionStatus === 'approve' && !validationChecked) {
      setAlertMessage(t('approvalDefinitions.confirmValidationToApprove'));
      return;
    }

    const versionId = currentVersion;
    if (!versionId) {
      setAlertMessage('No se encontró la versión del plan.');
      return;
    }

    try {
      await Promise.all(
        Object.entries(desistionByFocus).map(([focusId, decision]) =>
          addFocusDefinitionApproval(versionId, {
            APFocusId: parseInt(focusId, 10),
            Approved: decision === 'approve',
          }),
        ),
      );

      await addPlanDefinitionApproval(versionId, {
        Approved: decisionStatus === 'approve',
        CEOConfirmation: validationChecked,
        Comments: comments,
        IsDraft: false,
      });

      setAlertMessage(t('approvalDefinitions.approvalsProperlySync'));
      navigate('/');
    } catch (e) {
      console.error(e);
      setAlertMessage(t('approvalDefinitions.errorSavingApprovals'));
    }
  };

  const handleSaveProgress = async () => {
    const versionId = currentVersion;
    if (!versionId) {
      setAlertMessage(t('approvalDefinitions.planVersionNotFound'));
      return;
    }

    try {
      await addPlanDefinitionApproval(versionId, {
        Approved: decisionStatus === 'approve',
        CEOConfirmation: validationChecked,
        Comments: comments,
        IsDraft: true,
      });

      await Promise.all(
        Object.entries(desistionByFocus).map(([focusId, decision]) =>
          addFocusDefinitionApproval(versionId, {
            APFocusId: parseInt(focusId, 10),
            Approved: decision === 'approve',
          }),
        ),
      );

      setAlertMessage(t('approvalDefinitions.progressSavedShort'));
      navigate('/');
    } catch (e) {
      console.error(e);
      setAlertMessage(t('approvalDefinitions.errorSavingProgress'));
    }
  };

  const currentObjective =
    selectedFocus && selectedObjective
      ? (objectivesByFocus[selectedFocus.APFocusId] || []).find(
          (o) => o.APObjectiveId === selectedObjective,
        )
      : undefined;

  const rawActivities = currentObjective
    ? activitiesByObjective[currentObjective.APObjectiveId] || []
    : [];

  const getRequirementName = (activity: Activity): string => {
    if (!activity.ApplyRequirement || !activity.APRequirementId)
      return t('defineActivities.noApply');
    if (!selectedFocus) return '-----';
    const focusReq = visibleRequirements.find((r) => r.APFocusId === selectedFocus.APFocusId);
    if (!focusReq) return '-----';
    for (const rt of focusReq.RequirementTypes) {
      const req = rt.requirements.find((r) => r.APRequirementId === activity.APRequirementId);
      if (req) return req.Name;
    }
    return '-----';
  };

  const displayActivities = rawActivities.map((a: Activity) => ({
    id: a.APGroupActivityId,
    name: a.GroupActivityName,
    startDate: a.StartDate,
    endDate: a.EndDate,
    type: a.GroupActivityType,
    requirement: getRequirementName(a),
  }));

  const totalPages = Math.ceil(displayActivities.length / 5) || 1;
  const paginatedActivities = displayActivities.slice((currentPage - 1) * 5, currentPage * 5);

  const canContinue = !readOnlyMode
    ? Object.keys(desistionByFocus).length === focuses.length &&
      decisionStatus !== null &&
      (decisionStatus === 'reject' || (decisionStatus === 'approve' && validationChecked)) &&
      comments.trim().length > 0
    : true;
  const disableNext = !canContinue;
  const disableNextReason = React.useMemo(() => {
    if (readOnlyMode) return undefined;
    if (focuses.length > 0 && Object.keys(desistionByFocus).length !== focuses.length) {
      return t('approvalDefinitions.disableReasonCompleteFocusDecisions');
    }
    if (decisionStatus === null) {
      return t('approvalDefinitions.disableReasonSelectFinalDecision');
    }
    if (decisionStatus === 'approve' && !validationChecked) {
      return t('approvalDefinitions.mustConfirmValidation');
    }
    if (comments.trim().length === 0) {
      return t('approvalDefinitions.disableReasonCommentsRequired');
    }
    return undefined;
  }, [
    readOnlyMode,
    focuses.length,
    desistionByFocus,
    decisionStatus,
    validationChecked,
    comments,
    t,
  ]);
  const navigationDisableProps = React.useMemo(
    () => (disableNextReason ? { disableReason: disableNextReason } : {}),
    [disableNextReason],
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: 4 }}>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1366px', mx: 'auto' }}>
        {/* Main Card */}
        <Paper sx={{ borderRadius: 1, boxShadow: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Grid
            container
            sx={{
              pt: 2,
              px: { xs: 2, md: 3 },
              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
              flexWrap: 'wrap',
              gap: { xs: 2, md: 0 },
            }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Grid container spacing={2} sx={{ mb: { xs: 1, md: 2 } }} flexWrap="wrap">
                {/* Annual Plan Block */}
                <Grid
                  size={{ xs: 12, md: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('common.annualPlan')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {selectedYear}
                  </Typography>
                </Grid>

                {/* Scope Block */}
                <Grid
                  size={{ xs: 12, md: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeName}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('newAnnualPlan.annualPlanDefinition')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: { xs: 'auto', md: 0 },
                }}
              >
                <img src={PlanAnualIcon} alt="Plan Anual" style={{ width: 70, height: 70 }} />
              </Box>
            </Grid>
          </Grid>

          {/* Stepper */}
          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'white' }}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#D8FFEC',
                    color: '#333',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          bgcolor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto" sx={{ mt: 0.5 }}>
                          <Check />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step1')}
                      </Typography>
                      <Typography variant="caption">{t('common.step1Ttitle')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#D8FFEC',
                    color: '#333',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          bgcolor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto" sx={{ mt: 0.5 }}>
                          <Check />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step2')}
                      </Typography>
                      <Typography variant="caption">{t('common.step2Title')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#00BCE4',
                    color: 'white',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          color: '#00BCE4',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">3</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step3')}
                      </Typography>
                      <Typography variant="caption">{t('common.step3Title')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            {/* ponderado */}
            <Grid
              container
              spacing={2}
              sx={{ mb: 4, border: '1px solid #A5B2BB', borderRadius: 1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2,
                }}
              >
                <Grid
                  container
                  alignItems="center"
                  justifyContent={{ xs: 'center', md: 'space-between' }}
                  spacing={2}
                  size={12}
                  sx={{ flexWrap: 'wrap', rowGap: { xs: 2, md: 0 } }}
                >
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{
                      py: 1,
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {t('approvalDefinitions.definitionReview')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {t('approvalDefinitions.objectivesAndActivitiesByObjective')}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    justifyContent="flex-end"
                    size={{ xs: 12, md: 6 }}
                    sx={{
                      backgroundColor: '#F4F8F8',
                      borderRadius: 1,
                      px: { xs: 1.5, md: 2 },
                      mt: { xs: 2, md: 0 },
                    }}
                  >
                    <Grid
                      size={{ xs: 12, sm: 3, md: 1 }}
                      sx={{
                        py: { xs: 1, md: 0 },
                        display: 'flex',
                        justifyContent: { xs: 'center', md: 'flex-start' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: { xs: 'auto', md: 0 },
                        }}
                      >
                        <img
                          src={CalendarIcon}
                          alt="Calendario"
                          style={{ width: 60, height: 60 }}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 8 }}
                      sx={{
                        px: { xs: 1, md: 3 },
                        py: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('approvalDefinitions.totalActivitiesAdherencePctUltramar', {
                          enterpriseName,
                        })}
                      </Typography>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 3 }}
                      sx={{
                        textAlign: { xs: 'left', md: 'center' },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: '#79FCBD',
                          borderRadius: 1,
                          my: { xs: 1, md: 2 },
                          mr: { xs: 0, md: 2 },
                          py: 1,
                          display: 'inline-flex',
                          px: 2,
                        }}
                      >
                        {formatPercentage(globalAdherence, 1)}%
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* {t("approvalDefinitions.complianceByStrategicFocuses")} */}
            <Accordion
              expanded={expandedAccordion}
              onChange={() => setExpandedAccordion(!expandedAccordion)}
              sx={{
                mb: 2,
                boxShadow: 'none',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  borderRadius: 1,
                  border: '1px solid #A5B2BB',
                  minHeight: '40px !important',
                  height: '40px !important',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    margin: '8px 0 !important',
                  },
                  '&.Mui-expanded': {
                    minHeight: '40px !important',
                    height: '40px !important',
                  },
                  '& .MuiAccordionSummary-content.Mui-expanded': {
                    margin: '8px 0 !important',
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('approvalDefinitions.strategicFocuses')}
                </Typography>
              </AccordionSummary>
              {expandedAccordion && (
                <AccordionDetails
                  sx={{
                    bgcolor: 'white',
                    p: 0,
                    borderRadius: 1,
                    py: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    {focuses.map((foco, index) => (
                      <Grid
                        container
                        spacing={2}
                        key={foco.APFocusId}
                        sx={{
                          mb: 2,
                          mx: 1,
                          display: 'flex',
                          alignItems: 'stretch',
                        }}
                      >
                        {/* Paper principal del foco */}
                        <Grid
                          size={{ xs: 12, lg: 9 }}
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: '1px solid #A5B2BB',
                              borderRadius: 1,
                              bgcolor: '#F4F8F8',
                              width: '100%',
                              display: 'flex',
                              flexDirection: { xs: 'column', md: 'row' },
                              alignItems: { xs: 'flex-start', md: 'center' },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: { xs: 'flex-start', md: 'center' },
                                flexDirection: { xs: 'column', md: 'row' },
                                width: '100%',
                                gap: { xs: 2, md: 0 },
                              }}
                            >
                              {/* Contenido principal */}
                              <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 0, md: 2 } }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {t('approvalDefinitions.objectivesAndActivitiesByFocus')}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {index + 1}. {foco.Name}
                                </Typography>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                  <Grid size={{ xs: 12, sm: 'auto' }}>
                                    {(() => {
                                      const formatDate = (d?: string | null) => {
                                        if (!d) return null;
                                        const date = new Date(d);
                                        return date.toLocaleDateString(undefined, {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: '2-digit',
                                        });
                                      };
                                      const startDate = formatDate(foco.StartDate);
                                      const endDate = formatDate(foco.EndDate);
                                      const sep = t('approvalDefinitions.dateRangeSeparator');
                                      const rangeText =
                                        startDate && endDate
                                          ? `${startDate} ${sep} ${endDate}`
                                          : t('approvalDefinitions.noActivitiesDefined');

                                      return (
                                        <Box
                                          sx={{
                                            mt: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 1.5,
                                            py: 0.75,
                                            bgcolor: '#FFFFFF',
                                            border: '1px solid #A5B2BB',
                                            borderRadius: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 24,
                                              height: 24,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <img
                                              src={CalendarIcon}
                                              alt="Calendario"
                                              style={{ width: 20, height: 20 }}
                                            />
                                          </Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ color: '#333', fontWeight: 600, lineHeight: 1.2 }}
                                          >
                                            {rangeText}
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 'auto' }}>
                                    {(() => {
                                      const hasRequired = (foco.TotalRequired ?? 0) > 0;
                                      const pct = foco.AdherencePct ?? 0;
                                      const pctText = hasRequired
                                        ? `${formatPercentage(pct, 1)}%`
                                        : 'N/A';
                                      return (
                                        <Box
                                          sx={{
                                            mt: 1,
                                            bgcolor: '#CEFAFF',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 0.9,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            maxWidth: 240,
                                          }}
                                          title={
                                            !hasRequired
                                              ? t('approvalDefinitions.noMandatoryRequirements')
                                              : undefined
                                          }
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#333',
                                              lineHeight: 1,
                                              flex: 1,
                                            }}
                                          >
                                            {t(
                                              'approvalDefinitions.activitiesAdherencePctUltramar',
                                              { enterpriseName },
                                            )}
                                          </Typography>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: 700,
                                              color: hasRequired ? '#000' : '#777',
                                            }}
                                          >
                                            {pctText}
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </Grid>
                                </Grid>
                              </Box>

                              {/* Periodo */}
                              <Box
                                sx={{
                                  width: { xs: '100%', md: 180 },
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                  mt: { xs: 2, md: 0 },
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  disabled={isAnyLoading}
                                  onClick={() => handleOpenModal(foco, index)}
                                  size="small"
                                  sx={{
                                    borderColor: '#3A4048',
                                    color: '#3A4048',
                                    px: { xs: 2.5, md: 4 },
                                    py: 1,
                                    fontWeight: 600,
                                    borderRadius: 20,
                                    textTransform: 'none',
                                    width: { xs: '100%', md: 'auto' },
                                    '&:hover': {
                                      borderColor: '#212529ff',
                                      bgcolor: 'rgba(3, 22, 24, 0.04)',
                                    },
                                  }}
                                >
                                  {t('approvalDefinitions.viewDetail')}
                                </Button>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>

                        {/* Paper de aprobación */}
                        <Grid
                          size={{ xs: 12, lg: 3 }}
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: '1px solid #A5B2BB',
                              borderRadius: 1,
                              bgcolor: '#F4F8F8',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Grid
                              container
                              alignItems="center"
                              justifyContent="center"
                              spacing={1.5}
                              sx={{ flexWrap: 'wrap' }}
                            >
                              <Grid size={{ xs: 12, sm: 'auto' }} sx={{ mr: { xs: 0, sm: 2 } }}>
                                <Typography variant="subtitle2">
                                  {t('approvalDefinitions.approveTheseDefinitionsQuestion')}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 'auto' }}>
                                <RadioGroup
                                  value={desistionByFocus[foco.APFocusId] || 'reject'}
                                  key={foco.APFocusId}
                                  onChange={(e) => {
                                    const value = e.target.value as 'approve' | 'reject';

                                    setDesistionByFocus((prev) => ({
                                      ...prev,
                                      [foco.APFocusId]: value,
                                    }));
                                  }}
                                  sx={{ display: 'flex', flexDirection: 'row', gap: 1, px: 2 }}
                                >
                                  <FormControlLabel
                                    value="approve"
                                    disabled={!!readOnlyMode}
                                    control={<Radio size="small" />}
                                    label={t('common.approve')}
                                  />
                                  <FormControlLabel
                                    value="reject"
                                    disabled={!!readOnlyMode}
                                    control={<Radio size="small" />}
                                    label={t('common.reject')}
                                  />
                                </RadioGroup>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </AccordionDetails>
              )}
            </Accordion>

            {/* Annual Plan Decision Section */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                mt: 3,
                border: '1px solid #A5B2BB',
              }}
            >
              {/* Header con fondo cyan claro */}
              <Box
                sx={{
                  mb: 1,
                  bgcolor: '#CEFAFF',
                  borderRadius: 1,
                  m: 2,

                  p: 2.5,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('approvalDefinitions.approveAnnualPlanDefinitions')}
                </Typography>

                {/* Validación por parte del CEO text and toggle */}
                <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                      {t('approvalDefinitions.ceoValidation')}
                    </Typography>
                    <Typography variant="body2">
                      {tr('approvalDefinitions.ceoValidationQuestion')} {selectedYear}?
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <ToggleButtonGroup
                      value={decisionStatus}
                      exclusive
                      disabled={!!readOnlyMode}
                      onChange={(_, newValue) => newValue && setDecisionStatus(newValue)}
                      sx={{
                        bgcolor: '#A2F2FF',
                        border: '1px solid #00BCE4',
                        borderRadius: 20,
                        p: 0.5,
                        width: '100%',
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        display: 'flex',
                        '& .MuiToggleButton-root': {
                          flex: 1,
                          flexWrap: 'wrap',
                        },
                      }}
                    >
                      <ToggleButton
                        value="approve"
                        disabled={!allFocusApproved || !!readOnlyMode}
                        sx={{
                          px: 4,
                          py: 1,
                          borderRadius: 20,
                          textTransform: 'none',
                          border: 'none',
                          fontWeight: 500,
                          flexGrow: { xs: 1, sm: 0 },
                          justifyContent: 'center',
                          '&.Mui-selected': {
                            bgcolor: '#3A4048',
                            color: 'white',
                            '&:hover': {
                              bgcolor: '#424242',
                            },
                          },
                          '&:not(.Mui-selected)': {
                            bgcolor: 'transparent',
                            '&:hover': {
                              bgcolor: 'rgba(0, 105, 92, 0.08)',
                            },
                          },
                        }}
                      >
                        <Check sx={{ mr: 1, fontSize: 20 }} />
                        {t('approvalDefinitions.approve')}
                      </ToggleButton>
                      <ToggleButton
                        value="reject"
                        sx={{
                          px: 4,
                          py: 1,
                          borderRadius: 20,
                          textTransform: 'none',
                          border: 'none',
                          fontWeight: 500,
                          flexGrow: { xs: 1, sm: 0 },
                          justifyContent: 'center',
                          '&.Mui-selected': {
                            bgcolor: '#3A4048',
                            color: 'white',
                            '&:hover': {
                              bgcolor: '#424242',
                            },
                          },
                          '&:not(.Mui-selected)': {
                            bgcolor: 'transparent',
                            '&:hover': {
                              bgcolor: 'rgba(66, 66, 66, 0.08)',
                            },
                          },
                        }}
                      >
                        <Close sx={{ mr: 1, fontSize: 20 }} />
                        {t('approvalDefinitions.reject')}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
              </Box>
              <Box>
                {/* Conditional form based on decision */}
                {decisionStatus && (
                  <Box sx={{ mt: 3 }}>
                    {/* For Aprobar */}
                    {decisionStatus === 'approve' && (
                      <Paper
                        elevation={0}
                        sx={{ mb: 2, mx: 2, p: 2, bgcolor: '#F4F8F8', border: '1px solid #A5B2BB' }}
                      >
                        {/* Validation checkbox */}
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('approvalDefinitions.ceoValidationShort')}
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #A5B2BB' }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    disabled={!!readOnlyMode}
                                    checked={validationChecked}
                                    onChange={(e) => setValidationChecked(e.target.checked)}
                                    sx={{
                                      height: 40,
                                      width: 60,
                                    }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {t('approvalDefinitions.validationAnnualPlanDefinitions')}{' '}
                                      {selectedYear}
                                    </Typography>
                                    <Typography variant="subtitle2">
                                      {tr('approvalDefinitions.declareDefinitionsApproved', {
                                        year: selectedYear,
                                      })}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ alignItems: 'flex-start' }}
                              />
                            </Paper>
                          </Grid>

                          {/* Comments */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('approvalDefinitions.addComments')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              required
                              rows={3}
                              disabled={!!readOnlyMode}
                              placeholder={t('approvalDefinitions.commentsPlaceholder')}
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'white',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    )}

                    {/* For Rechazar */}
                    {decisionStatus === 'reject' && (
                      <>
                        {/* Comments */}
                        <Grid size={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              mb: 2,
                              mx: 2,
                              p: 2,
                              bgcolor: '#F4F8F8',
                              border: '1px solid #A5B2BB',
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('approvalDefinitions.addComments')}
                            </Typography>
                            <TextField
                              required
                              fullWidth
                              multiline
                              disabled={!!readOnlyMode}
                              rows={3}
                              placeholder={t('approvalDefinitions.commentsPlaceholder')}
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'white',
                                },
                              }}
                            />
                          </Paper>
                        </Grid>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Action buttons */}
            <Navigation
              isAnyLoading={isAnyLoading}
              onSaveDraft={() => handleSaveProgress()}
              saveDraftTitle={t('approvalDefinitions.saveProgress')}
              onNext={() => handleSaveAndFinalize()}
              disableNext={disableNext}
              onExit={() => handleExit()}
              exitTitle={t('common.exit')}
              nextTitle={
                readOnlyMode && currentFulfillmentVersion
                  ? t('approvalDefinitions.goToDefineActivities')
                  : readOnlyMode && !currentFulfillmentVersion
                    ? t('approvalDefinitions.finish')
                    : t('approvalDefinitions.saveAndFinalize')
              }
              readOnlyMode={readOnlyMode}
              disableSaveDraft={false}
              {...navigationDisableProps}
            />
          </Box>
        </Paper>
      </Box>

      {/* Detail Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, bgcolor: '#F4F8F8' }}>
          <Typography variant="subtitle1" component="div" lineHeight={1.5}>
            {t('approvalDefinitions.strategicFocus')}
          </Typography>
          <Typography variant="subtitle1" component="div" lineHeight={1} sx={{ fontWeight: 600 }}>
            {selectedIndex ? selectedIndex + 1 : 1}. {selectedFocus?.Name}
          </Typography>
          <IconButton
            aria-label="close"
            disabled={isAnyLoading}
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 16,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Alert Message */}
          {alertMessage && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: '#FEFFC2',
                borderLeft: '4px solid #C99D00',
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                width: '100%',
                flexShrink: 0,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 0 },
              }}
            >
              <Typography variant="body2">{alertMessage}</Typography>
              <IconButton
                size="small"
                onClick={() => setAlertMessage(null)}
                disabled={isAnyLoading}
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
          {selectedFocus && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2, flex: 1, overflow: 'hidden' }}>
                <Grid
                  size={{ xs: 12, lg: 4 }}
                  sx={{
                    overflow: 'hidden',
                    p: 2,
                    border: '1px solid #A5B2BB',
                    height: { xs: 'auto', lg: '100%' },
                    overflowY: 'auto',
                    borderRadius: 1,
                  }}
                >
                  <Accordion
                    expanded={true}
                    sx={{
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      bgcolor: 'white',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        borderRadius: 1,
                        height: '100%',
                        bgcolor: '#e8f9fb',
                        borderLeft: '4px solid #00BCE4',
                        '& .MuiAccordionSummary-content': {
                          my: 1,
                        },
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {t('approvalDefinitions.objectivesByStrategicFocus')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <List>
                        {(objectivesByFocus[selectedFocus.APFocusId] || []).map((objetivo) => (
                          <ListItem key={objetivo.APObjectiveId} disablePadding>
                            <ListItemButton
                              selected={objetivo.APObjectiveId === selectedObjective}
                              disabled={isAnyLoading}
                              onClick={() => {
                                setSelectedObjective(objetivo.APObjectiveId);
                                getActivitiesByObjectiveId(objetivo.APObjectiveId);
                                setCurrentPage(1);
                              }}
                              sx={{
                                borderRadius: 1,
                                '&.Mui-selected': {
                                  backgroundColor: '#E8F5F5',
                                  '&:hover': {
                                    backgroundColor: '#E8F5F5',
                                  },
                                },
                              }}
                            >
                              <ListItemIcon>
                                <ChevronRight />
                              </ListItemIcon>
                              <Tooltip title={objetivo.Name} placement="right">
                                <ListItemText
                                  primary={objetivo.Name}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    style: {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    },
                                  }}
                                />
                              </Tooltip>
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid
                  size={{ xs: 12, lg: 8 }}
                  sx={{
                    overflow: 'hidden',
                    p: 2,
                    border: '1px solid #A5B2BB',
                    height: { xs: 'auto', lg: '100%' },
                    overflowY: 'auto',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('approvalDefinitions.objectiveActivities')}
                  </Typography>
                  {currentObjective && (
                    <>
                      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'transparent' }}>
                              {[
                                t('table.activityName'),
                                t('table.activityTerm'),
                                t('table.activityType'),
                                t('table.activityRequirement'),
                              ].map((label, idx) => (
                                <TableCell
                                  key={label}
                                  align={idx === 5 ? 'center' : 'left'}
                                  sx={{
                                    p: 0.25,
                                    borderBottom: 'none',
                                    bgcolor: 'transparent',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      bgcolor: '#DEEAEB',
                                      border: `1px solid #d5e2e7`,
                                      px: 1,
                                      py: 1,
                                      height: 48,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderTopLeftRadius: 8,
                                      borderTopRightRadius: 8,
                                      boxShadow:
                                        'inset 0 1px 0 rgba(255,255,255,0.85), 0 1px 2px rgba(0,0,0,0.06)',
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#333',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {label}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedActivities.map((actividad, index, arr) => {
                              const rowBg = index % 2 === 0 ? '#EFF5F5' : '#FAFCFC';
                              const isLastRow = index === arr.length - 1;
                              const innerCommon = {
                                border: `1px solid #e1eaee`,
                                px: 1,
                                py: 1,
                                bgcolor: rowBg,
                                boxShadow:
                                  'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 1px rgba(0,0,0,0.04)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 48,
                                ...(isLastRow && {
                                  borderBottomLeftRadius: 8,
                                  borderBottomRightRadius: 8,
                                }),
                              } as const;

                              return (
                                <TableRow key={actividad.id} sx={{ bgcolor: 'transparent' }}>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box sx={innerCommon}>
                                      <Tooltip title={actividad.name} placement="top">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '200px',
                                            cursor: 'default',
                                          }}
                                        >
                                          {actividad.name.length > 120
                                            ? `${actividad.name.substring(0, 120)}...`
                                            : actividad.name}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box sx={innerCommon}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {actividad.startDate && actividad.endDate
                                          ? `${new Date(actividad.startDate).toLocaleDateString()} ${t('pdf.separator')} ${new Date(actividad.endDate).toLocaleDateString()}`
                                          : `- ${t('pdf.separator')} -`}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box
                                      sx={{
                                        ...innerCommon,
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: '#F4F8F8',
                                          border: '1px solid #A5B2BB',
                                          px: 1.5,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          fontSize: '0.8125rem',
                                          minHeight: 30,
                                          fontWeight: 400,
                                          textAlign: 'center',
                                          borderRadius: 1,
                                        }}
                                      >
                                        {actividad.type === ActivityTypes.STRATEGIC
                                          ? t('defineActivities.strategic')
                                          : t('defineActivities.permanent')}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box sx={innerCommon}>
                                      {/* Requerimiento con truncado y tooltip */}
                                      <Tooltip title={actividad.requirement} placement="top">
                                        <Typography
                                          variant="body2"
                                          textAlign={'center'}
                                          sx={{
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '200px',
                                            cursor: 'default',
                                          }}
                                        >
                                          {actividad.requirement.length > 120
                                            ? `${actividad.requirement.substring(0, 120)}...`
                                            : actividad.requirement}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            siblingCount={1}
                            boundaryCount={1}
                            shape="rounded"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ApprovalDefinitions;
